import classes from '@/containers/pages/commonSettings/tabs/StoragePage/styles.module.css';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Checkbox,
  Flex,
  Loader,
  Select,
  Stack,
  TextInput,
  Tooltip
} from '@mantine/core';
import { IconChevronDown, IconAlertCircle } from '@tabler/icons-react';
import { IUsePopUpUI } from '@components/UI/PopUp';
import { storageConfigurationApiArea } from '@/services/api/storageConfigurationApiArea';
import { StorageType } from '@/services/api/storageConfigurationApiArea/types';

type EditStorageDialogProps = {
  itemToEditId: number;
  onSuccess?: () => void;
} & IUsePopUpUI;

const EditStorageDialog = (props: EditStorageDialogProps) => {
  const { t } = useTranslation();
  const { itemToEditId, handleClosePopUp } = props;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);

  // Состояния для валидации
  const [isValidating, setIsValidating] = useState(false);
  const [isValidated, setIsValidated] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const TypeOptions = [
    {
      label: t('storagePage.storageTypes.s3'),
      value: 'S3'
    },
    {
      label: t('storagePage.storageTypes.fileStorage'),
      value: 'Fs'
    }
  ];

  // Общие поля
  const [name, setName] = useState<string>('');
  const [baseType, setBaseType] = useState<'S3' | 'Fs'>('S3');
  const [isEncrypted, setIsEncrypted] = useState<boolean>(false);

  // Поля для S3
  const [s3Key, setS3Key] = useState<string>('');
  const [s3Secret, setS3Secret] = useState<string>('');
  const [s3SystemName, setS3SystemName] = useState<string>('');
  const [s3Url, setS3Url] = useState<string>('');
  const [s3Bucket, setS3Bucket] = useState<string>('');

  // Поля для Fs
  const [fsUrl, setFsUrl] = useState<string>('');

  useEffect(() => {
    const fetchStorage = async () => {
      try {
        setIsFetching(true);
        const data = await storageConfigurationApiArea().GetStorageConfigurationById(itemToEditId);

        // Заполняем форму данными
        setName(data.Name);

        // Определяем базовый тип и шифрование
        if (data.Type === StorageType.S3 || data.Type === StorageType.S3Encrypted) {
          setBaseType('S3');
          setIsEncrypted(data.Type === StorageType.S3Encrypted);
        } else {
          setBaseType('Fs');
          setIsEncrypted(data.Type === StorageType.FsEncrypted);
        }

        if (data.S3ConfigurationSettings) {
          setS3Key(data.S3ConfigurationSettings.Key);
          setS3Secret(data.S3ConfigurationSettings.Secret);
          setS3SystemName(data.S3ConfigurationSettings.SystemName);
          setS3Bucket(data.S3ConfigurationSettings.Bucket);
          setS3Url(data.S3ConfigurationSettings.Url);
        }

        if (data.FsConfigurationSettings) {
          setFsUrl(data.FsConfigurationSettings.Host);
        }
      } catch (error) {
        // Игнорируем ошибку
      } finally {
        setIsFetching(false);
      }
    };

    if (itemToEditId) {
      fetchStorage();
    }
  }, [itemToEditId]);

  const onChange = (value: string | null) => {
    if (value === 'S3' || value === 'Fs') {
      setBaseType(value);
      resetValidation();
    }
  };

  // Функция для сброса валидации при изменении полей
  const resetValidation = () => {
    setIsValidated(false);
    setValidationError(null);
  };

  const getBaseStorageType = (): StorageType.S3 | StorageType.Fs => {
    return baseType === 'S3' ? StorageType.S3 : StorageType.Fs;
  };

  const isS3Type = baseType === 'S3';
  const isFsType = baseType === 'Fs';

  const handleValidate = async (): Promise<boolean> => {
    try {
      setIsValidating(true);
      setValidationError(null);

      const storageType = getBaseStorageType();

      let result;
      if (isS3Type) {
        result = await storageConfigurationApiArea().ValidateStorageConfiguration({
          Name: name,
          Type: storageType as StorageType.S3,
          S3ConfigurationSettings: {
            Key: s3Key,
            Secret: s3Secret,
            SystemName: s3SystemName,
            Bucket: s3Bucket,
            Url: s3Url
          },
          IsEncrypted: isEncrypted
        });
      } else if (isFsType) {
        result = await storageConfigurationApiArea().ValidateStorageConfiguration({
          Name: name,
          Type: storageType as StorageType.Fs,
          FsConfigurationSettings: {
            Host: fsUrl
          },
          IsEncrypted: isEncrypted
        });
      } else {
        return false;
      }

      if (result.Success) {
        setIsValidated(true);
        setValidationError(null);
        return true;
      } else {
        setIsValidated(false);

        // Формируем сообщение об ошибке из Details
        let errorMessage = 'Validation failed';
        if (result.Details && result.Details.Errors.length > 0) {
          errorMessage = result.Details.Errors.join('; ');
        } else if (result.FailedValidation.length > 0) {
          errorMessage = `Validation failed for: ${result.FailedValidation.join(', ')}`;
        }

        setValidationError(errorMessage);
        return false;
      }
    } catch (error) {
      setIsValidated(false);
      setValidationError(error instanceof Error ? error.message : 'Unknown validation error');
      return false;
    } finally {
      setIsValidating(false);
    }
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);

      // Если валидация еще не была выполнена, выполняем ее сначала
      if (!isValidated) {
        const validationResult = await handleValidate();

        // Проверяем результат валидации
        if (!validationResult) {
          setIsLoading(false);
          return;
        }
      }

      const storageType = getBaseStorageType();

      if (isS3Type) {
        await storageConfigurationApiArea().UpdateStorageConfiguration({
          id: itemToEditId,
          Name: name,
          Type: storageType as StorageType.S3,
          S3ConfigurationSettings: {
            Key: s3Key,
            Secret: s3Secret,
            SystemName: s3SystemName,
            Bucket: s3Bucket,
            Url: s3Url
          },
          IsEncrypted: isEncrypted
        });
      } else if (isFsType) {
        await storageConfigurationApiArea().UpdateStorageConfiguration({
          id: itemToEditId,
          Name: name,
          Type: storageType as StorageType.Fs,
          FsConfigurationSettings: {
            Host: fsUrl
          },
          IsEncrypted: isEncrypted
        });
      }

      if (props.onSuccess) {
        await props.onSuccess();
      }
      handleClosePopUp();
    } catch (error) {
      // Игнорируем ошибку
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    handleClosePopUp();
  };

  const isFormValid = () => {
    if (!name) return false;

    if (isS3Type) {
      return s3Key && s3Secret && s3SystemName && s3Bucket && s3Url;
    }

    if (isFsType) {
      return fsUrl;
    }

    return false;
  };

  // Получить текст тултипа для кнопки валидации
  const getValidateTooltip = () => {
    if (!isFormValid()) {
      return t('storagePage.editDialog.tooltips.fillRequiredFields');
    }
    return '';
  };

  // Получить текст тултипа для кнопки сохранения
  const getSaveTooltip = () => {
    if (!isFormValid()) {
      return t('storagePage.editDialog.tooltips.fillRequiredFields');
    }
    if (!isValidated) {
      return t('storagePage.editDialog.tooltips.validateFirst');
    }
    return '';
  };

  if (isFetching) {
    return (
      <div className={classes.container}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
          <Loader color="blue" />
        </div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <div className={classes.form}>
        <Stack gap="md">
          <Flex justify="space-between" gap="md">
            <Select
              style={{ flex: 1 }}
              value={baseType}
              rightSection={<IconChevronDown size="1rem" color="#98A2B3" />}
              onChange={onChange}
              data={TypeOptions}
              label={t('storagePage.createDialog.typeLabel')}
              placeholder={t('storagePage.createDialog.typePlaceholder')}
              comboboxProps={{
                transitionProps: { transition: 'pop', duration: 200 },
                offset: -20,
                withinPortal: false
              }}
              disabled={true}
            />
            <TextInput
              style={{ flex: 1 }}
              placeholder={t('storagePage.createDialog.namePlaceholder')}
              label={t('storagePage.createDialog.nameLabel')}
              withAsterisk
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                resetValidation();
              }}
            />
          </Flex>

          {isS3Type && (
            <>
              <TextInput
                placeholder={t('storagePage.createDialog.s3KeyPlaceholder')}
                label={t('storagePage.createDialog.s3KeyLabel')}
                withAsterisk
                value={s3Key}
                onChange={(e) => {
                  setS3Key(e.target.value);
                  resetValidation();
                }}
              />
              <TextInput
                placeholder={t('storagePage.createDialog.s3SecretPlaceholder')}
                label={t('storagePage.createDialog.s3SecretLabel')}
                withAsterisk
                type="password"
                value={s3Secret}
                onChange={(e) => {
                  setS3Secret(e.target.value);
                  resetValidation();
                }}
              />
              <TextInput
                placeholder={t('storagePage.createDialog.s3SystemNamePlaceholder')}
                label={t('storagePage.createDialog.s3SystemNameLabel')}
                withAsterisk
                value={s3SystemName}
                onChange={(e) => {
                  setS3SystemName(e.target.value);
                  resetValidation();
                }}
              />
              <TextInput
                placeholder={t('storagePage.createDialog.s3UrlPlaceholder')}
                label={t('storagePage.createDialog.s3UrlLabel')}
                withAsterisk
                value={s3Url}
                onChange={(e) => {
                  setS3Url(e.target.value);
                  resetValidation();
                }}
              />
              <TextInput
                placeholder={t('storagePage.createDialog.s3BucketPlaceholder')}
                label={t('storagePage.createDialog.s3BucketLabel')}
                withAsterisk
                value={s3Bucket}
                onChange={(e) => {
                  setS3Bucket(e.target.value);
                  resetValidation();
                }}
              />
            </>
          )}

          {isFsType && (
            <TextInput
              placeholder={t('storagePage.createDialog.fsUrlPlaceholder')}
              label={t('storagePage.createDialog.fsUrlLabel')}
              withAsterisk
              value={fsUrl}
              onChange={(e) => {
                setFsUrl(e.target.value);
                resetValidation();
              }}
            />
          )}

          <Checkbox
            label={t('storagePage.createDialog.enableEncryption')}
            checked={isEncrypted}
            onChange={(e) => {
              setIsEncrypted(e.currentTarget.checked);
              resetValidation();
            }}
            disabled={true}
          />

          {/* Отображение ошибок валидации */}
          {validationError && (
            <Alert icon={<IconAlertCircle size="1rem" />} color="red" variant="light">
              {validationError}
            </Alert>
          )}

          {/* Отображение успешной валидации */}
          {isValidated && !validationError && (
            <Alert color="green" variant="light">
              {t('storagePage.createDialog.validationSuccess')}
            </Alert>
          )}
        </Stack>
      </div>

      <div className={classes.actionsWrapper}>
        <div className={classes.actions}>
          <Tooltip
            label={getSaveTooltip()}
            disabled={!getSaveTooltip() || isLoading}
            withArrow
            position="top"
          >
            <Button
              maw="fit-content"
              fullWidth={true}
              onClick={handleSave}
              variant="light"
              disabled={!isFormValid() || !isValidated || isLoading}
              loading={isLoading}
            >
              {t('storagePage.editDialog.saveButton')}
            </Button>
          </Tooltip>
          <Button
            maw="fit-content"
            fullWidth={true}
            onClick={handleCancel}
            variant="light"
            color="gray"
            disabled={isLoading}
          >
            {t('storagePage.editDialog.cancelButton')}
          </Button>
        </div>
        <Tooltip
          label={getValidateTooltip()}
          disabled={!getValidateTooltip() || isValidating || isLoading}
          withArrow
          position="top"
        >
          <Button
            fullWidth={false}
            onClick={handleValidate}
            variant="outline"
            color="blue"
            disabled={!isFormValid() || isValidating || isLoading}
            loading={isValidating}
          >
            {t('storagePage.createDialog.validateButton')}
          </Button>
        </Tooltip>
      </div>
    </div>
  );
};

export default EditStorageDialog;
